import React from 'react';
import dayjs from 'dayjs';
import type { InspectionRecord } from '@/services/quality.service';
import type { ApprovalPrintSigner } from '@/services/approval.service';
import { EngineCommercialA4Print } from '@/components/print/EngineCommercialPrints';
import { PrintableApprovalSigners } from '@/components/approval/PrintableApprovalSigners';

export type ReceivingDimPrintCompanyInfo = {
  name: string;
  legalName?: string;
  address: string;
};

type SampleRow = {
  sample_number?: number;
  sample_qty?: number;
  status?: string;
  notes?: string | null;
};

export type ReceivingDimensionalPrintRecord = InspectionRecord & {
  lot_quantity?: number | null;
  sample_size?: number | null;
  sampling_plan?: string | null;
  pending_result?: string | null;
  samples?: SampleRow[];
  parent_receiving_inspection?: {
    id: string;
    inspection_number: string;
    status: string;
    result: string;
  };
  grn?: {
    grn_number?: string;
    grn_status?: string;
    inspection_status?: string;
  };
};

function dash(v?: string | number | null): string {
  if (v == null || String(v).trim() === '') return '—';
  return String(v);
}

function formatDate(v?: string | null): string {
  if (!v) return '—';
  return dayjs(v).format('DD MMM YYYY');
}

export function ReceivingDimensionalInspectionPrintView({
  record,
  companyInfo,
  approvalSigners = [],
}: {
  record: ReceivingDimensionalPrintRecord;
  companyInfo: ReceivingDimPrintCompanyInfo;
  approvalSigners?: ApprovalPrintSigner[];
}) {
  const legal = companyInfo.legalName || companyInfo.name;
  const samples = record.samples || [];
  const parent = record.parent_receiving_inspection;

  const metaLines = [
    { label: 'Inspection No', value: dash(record.inspection_number) },
    { label: 'Date', value: formatDate(record.actual_date || record.scheduled_date) },
    { label: 'Status', value: dash(record.status) },
    { label: 'Result', value: dash(record.result || record.pending_result) },
    { label: 'Receiving', value: dash(parent?.inspection_number) },
    { label: 'GRN', value: dash(record.grn?.grn_number) },
    { label: 'Lot qty', value: dash(record.lot_quantity) },
    { label: 'Samples taken', value: dash(record.sample_size) },
    { label: 'Sampling plan', value: dash(record.sampling_plan) },
  ];

  const sampleRows = samples.map((s, i) => ({
    id: `s-${i}`,
    lineNumber: s.sample_number ?? i + 1,
    description: dash(s.notes) === '—' ? `Sample ${s.sample_number ?? i + 1}` : dash(s.notes),
    cells: {
      no: String(s.sample_number ?? i + 1),
      qty: dash(s.sample_qty),
      status: dash(s.status),
      notes: dash(s.notes),
    },
  }));

  return (
    <EngineCommercialA4Print
      title="Receiving Dimension Inspection Note"
      documentNumber={record.inspection_number}
      partyName={legal}
      metaLines={metaLines}
      introText={
        record.findings || record.observations
          ? `Findings / observations: ${[record.findings, record.observations].filter(Boolean).join(' | ')}`
          : 'Dimensional check of received material (Pass/Fail).'
      }
      columns={[
        { key: 'no', label: 'Sample #', width: '70px' },
        { key: 'qty', label: 'Qty', width: '70px', align: 'right' },
        { key: 'status', label: 'Status', width: '90px' },
        { key: 'notes', label: 'Notes', width: '240px' },
      ]}
      lines={
        sampleRows.length
          ? sampleRows
          : [
              {
                id: 'empty',
                lineNumber: '—',
                description: 'No samples recorded',
                cells: { no: '—', qty: '—', status: '—', notes: 'No samples recorded' },
              },
            ]
      }
      finals={[{ id: 'signers', heightPx: 160 }]}
      finalBlocks={{
        signers: <PrintableApprovalSigners signers={approvalSigners} />,
      }}
      letterhead
      docType="receiving_dimensional_inspection"
    />
  );
}

export default ReceivingDimensionalInspectionPrintView;
